import React, {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';

import {useEventTracking} from 'src/hooks/useEventTracking';
import {useUserAlerts} from 'src/modules/profile/user/tabs/content/alerts/hooks/useUserAlerts';
import {useUserLayoutsCount} from 'src/modules/profile/user/tabs/content/layouts/hooks/useUserLayoutsCount';
import {useUserListsCount} from 'src/modules/profile/user/tabs/content/lists/hooks/useUserListsCount';
import {useUserSearchesCount} from 'src/modules/profile/user/tabs/content/searches/hooks/useUserSearchesCount';
import {useUserWorkspaces} from 'src/modules/profile/user/tabs/content/workspace/hooks/useUserWorkspaces';

import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';
import {SecondaryTabs, Tab, TabBadge} from '@pb/kit-component-tabs-v2';


type Props = {
    userId: number,
    activeTab: string,
};

export const CONTENT_SUBTAB_SCOPE = 'user-content-subtab-counter';

export const ContentTabs = ({userId, activeTab}: Props) => {
    const navigate = useNavigate();
    const {userContentTabChange} = useEventTracking();

    const {
        isFetching: searchesLoading,
        total: savedSearchesCount,
    } = useUserSearchesCount({scopeKey: CONTENT_SUBTAB_SCOPE, userId});

    const {
        isFetching: listsLoading,
        total: savedListsCount,
    } = useUserListsCount({scopeKey: CONTENT_SUBTAB_SCOPE, userId});

    const {
        isFetching: workspacesLoading,
        total: workspacesTotal,
    } = useUserWorkspaces({scopeKey: CONTENT_SUBTAB_SCOPE, userId});

    const {
        isFetching: alertsLoading,
        total: alertsTotal,
    } = useUserAlerts({scopeKey: CONTENT_SUBTAB_SCOPE, userId});

    const {
        isFetching: layoutsLoading,
        total: layoutsTotal,
    } = useUserLayoutsCount({userId, autoload: true});

    let searchesBadge: React.ReactNode = undefined;
    if (searchesLoading) {
        searchesBadge = <Loading type={LoadingTypes.ICON}/>;
    } else if (savedSearchesCount !== undefined) {
        searchesBadge = `(${savedSearchesCount})`;
    }

    let listsBadge: React.ReactNode = undefined;
    if (listsLoading) {
        listsBadge = <Loading type={LoadingTypes.ICON}/>;
    } else if (savedListsCount !== undefined) {
        listsBadge = `(${savedListsCount})`;
    }

    let alertBadge: React.ReactNode = undefined;
    if (alertsLoading) {
        alertBadge = <Loading type={LoadingTypes.ICON}/>;
    } else if (alertsTotal !== undefined) {
        alertBadge = `(${alertsTotal})`;
    }

    let workspacesBadge: React.ReactNode = undefined;
    if (workspacesLoading) {
        workspacesBadge = <Loading type={LoadingTypes.ICON}/>;
    } else if (workspacesTotal !== undefined) {
        workspacesBadge = `(${workspacesTotal})`;
    }

    let layoutsBadge: React.ReactNode = undefined;
    if (layoutsLoading) {
        layoutsBadge = <Loading type={LoadingTypes.ICON}/>;
    } else if (layoutsTotal !== undefined) {
        layoutsBadge = `(${layoutsTotal})`;
    }

    const changeTab = useCallback((tab: string) => {
        userContentTabChange({activeTab: tab, previousTab: activeTab});
        navigate(`/profile/user/${userId}/content/${tab}`);
    }, [userId, navigate, activeTab, userContentTabChange]);


    return (
        <SecondaryTabs value={activeTab} onChange={changeTab} className="level-2-tabs" style={{width: '100%'}}>
            <Tab endAdornment={<TabBadge>{searchesBadge}</TabBadge>} caption="Searches" value="searches"/>
            <Tab endAdornment={<TabBadge>{listsBadge}</TabBadge>} caption="Lists" value="lists"/>
            <Tab endAdornment={<TabBadge>{alertBadge}</TabBadge>} caption="Alerts" value="alerts"/>
            <Tab endAdornment={<TabBadge>{workspacesBadge}</TabBadge>} caption="Workspaces" value="workspaces"/>
            <Tab endAdornment={<TabBadge>{layoutsBadge}</TabBadge>} caption="Layouts" value="layouts"/>
        </SecondaryTabs>
    );
};
